import type { MessageFieldsFragment } from '#src/graphql/generated';

import { useMutation } from '@apollo/client/react';
import {
  Badge,
  Box,
  ButtonGroup,
  HStack,
  IconButton,
  Menu,
  Portal,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import MDEditor from '@uiw/react-md-editor';
import { FaExpand, FaFont, FaMicrophone, FaRobot } from 'react-icons/fa';
import { FiMoreVertical } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { useColorMode } from '#src/components/ui/color-mode';
import { toaster } from '#src/components/ui/toaster';
import { DELETE_MESSAGE } from '#src/graphql/mutations/delete-message';
import { useUser } from '#src/providers/user-provider';
import { summarizeMessageForThreadPreview } from '#src/utils/message-thread-focus';

/** Variables passed to the chat-message-query processor from the webapp. */
export type ChatMessageQueryOptions = {
  iterative?: boolean;
};

function getSourceIcon(source: string) {
  switch (source) {
    case 'text':
      return FaFont;
    case 'audio':
      return FaMicrophone;
    case 'ai-note':
      return FaRobot;
    default:
      return null;
  }
}

export type MessageCardVariant = 'collapsed' | 'expanded';

function formatMessageTimestamp(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

interface MessageCardProps {
  message: MessageFieldsFragment;
  childMessages?: MessageFieldsFragment[];
  variant: MessageCardVariant;
  /** Collapsed: expand thread in URL. Omitted for summary rows. */
  onExpandThread?: (rootId: string) => void;
  onRunChatMessageQuery: (
    messageId: string,
    query: string,
    options?: ChatMessageQueryOptions,
  ) => Promise<void>;
  chatMessageQueryLoadingId: string | null;
  onMessageDeleted?: () => void | Promise<void>;
}

export function MessageCard({
  message,
  childMessages,
  variant,
  onExpandThread,
  onRunChatMessageQuery,
  chatMessageQueryLoadingId,
  onMessageDeleted,
}: MessageCardProps) {
  const { colorMode } = useColorMode();
  const { user } = useUser();
  const [deleteMessage, { loading: deleteLoading }] =
    useMutation(DELETE_MESSAGE);

  const canDeleteMessage = Boolean(
    user &&
      (message.userId === user.id ||
        (message.userId === 'system' && message.targetUserId === user.id)),
  );

  const handleDeleteMessage = async () => {
    try {
      await deleteMessage({ variables: { input: { id: message.id } } });
      await onMessageDeleted?.();
      toaster.create({
        title: 'Message deleted',
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: 'Could not delete message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const documentContext = message.messageContexts?.find(
    (ctx) => ctx.name === 'documentId' && ctx.document,
  );
  const document = documentContext?.document;

  const isSystemMessage = message.userId === 'system';
  const isNoteLoading = chatMessageQueryLoadingId === message.id;
  const isRoot = message.parentMessageId == null;
  const showExpand =
    variant === 'collapsed' && isRoot && Boolean(onExpandThread);

  return (
    <Box>
      <Box
        p={4}
        backgroundColor="var(--background-base-highlight-color)"
        borderRadius="md"
        borderColor="var(--border-base-color)"
        borderWidth="var(--border-base-width)"
      >
        <HStack justify="space-between" align="start" mb={2}>
          <HStack gap={2} align="center" flexWrap="wrap">
            <Text
              fontSize="xs"
              fontWeight="medium"
              color={isSystemMessage ? 'var(--brand-base-color)' : 'fg'}
            >
              {message.author?.displayName ?? 'Unknown'}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              ·
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {formatMessageTimestamp(message.createdAt)}
            </Text>
            {getSourceIcon(message.source) && (
              <Box
                as={getSourceIcon(message.source)!}
                aria-label={message.source}
                color={
                  message.source === 'ai-note'
                    ? 'var(--brand-base-color)'
                    : 'fg.subtle'
                }
                fontSize="xs"
                display="inline-flex"
              />
            )}
            {document && (
              <Link
                to={`/documents/${document.path}`}
                style={{ textDecoration: 'none' }}
              >
                <Badge
                  as="span"
                  colorPalette="blue"
                  variant="subtle"
                  size="sm"
                  cursor="pointer"
                  _hover={{ opacity: 0.9 }}
                >
                  View Document ➡️
                </Badge>
              </Link>
            )}
          </HStack>
          <ButtonGroup size="xs" variant="outline" attached align="center">
            {!isSystemMessage && (
              <IconButton
                aria-label="Run AI note using this message as the prompt"
                onClick={() =>
                  void onRunChatMessageQuery(message.id, message.value, {
                    iterative: true,
                  })
                }
                disabled={isNoteLoading}
              >
                {isNoteLoading ? <Spinner size="sm" /> : '@'}
              </IconButton>
            )}
            {showExpand && onExpandThread && (
              <IconButton
                aria-label="Expand thread"
                onClick={() => onExpandThread(message.id)}
              >
                <FaExpand />
              </IconButton>
            )}
            {canDeleteMessage && (
              <Menu.Root positioning={{ placement: 'bottom-end' }}>
                <Menu.Trigger asChild>
                  <IconButton
                    aria-label="Message actions"
                    disabled={deleteLoading}
                  >
                    <FiMoreVertical />
                  </IconButton>
                </Menu.Trigger>
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content>
                      <Menu.Item
                        value="delete"
                        color="fg.error"
                        onClick={() => void handleDeleteMessage()}
                      >
                        Delete
                      </Menu.Item>
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            )}
          </ButtonGroup>
        </HStack>
        {isSystemMessage ? (
          <Box
            data-color-mode={colorMode}
            fontSize="sm"
            maxW="100%"
            overflowX="auto"
            color="var(--font-base-color)"
          >
            <MDEditor.Markdown source={message.value} />
          </Box>
        ) : (
          <Text
            fontSize="sm"
            whiteSpace="pre-wrap"
            color="var(--font-base-color)"
          >
            {message.value}
          </Text>
        )}
      </Box>

      {variant === 'collapsed' && childMessages && childMessages.length > 0 && (
        <VStack
          gap={2}
          align="stretch"
          mt={2}
          pl={6}
          borderLeftWidth="2px"
          borderLeftColor="var(--border-base-color)"
        >
          {childMessages.map((child) => (
            <CollapsedChildSummary key={child.id} message={child} />
          ))}
        </VStack>
      )}

      {variant === 'expanded' && childMessages && childMessages.length > 0 && (
        <VStack
          gap={2}
          align="stretch"
          mt={2}
          pl={6}
          borderLeftWidth="2px"
          borderLeftColor="var(--border-base-color)"
        >
          {childMessages.map((child) => (
            <MessageCard
              key={child.id}
              message={child}
              variant="expanded"
              onRunChatMessageQuery={onRunChatMessageQuery}
              chatMessageQueryLoadingId={chatMessageQueryLoadingId}
              onMessageDeleted={onMessageDeleted}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
}

function CollapsedChildSummary({
  message,
}: {
  message: MessageFieldsFragment;
}) {
  const isSystemMessage = message.userId === 'system';
  const preview = summarizeMessageForThreadPreview(message.value);

  return (
    <Box py={1}>
      <HStack gap={2} align="center" flexWrap="wrap" minW={0} mb={1}>
        <Text
          fontSize="xs"
          fontWeight="medium"
          color={isSystemMessage ? 'var(--brand-base-color)' : 'fg.muted'}
        >
          {message.author?.displayName ?? 'Unknown'}
        </Text>
        <Text fontSize="xs" color="fg.muted">
          ·
        </Text>
        <Text fontSize="xs" color="fg.muted">
          {formatMessageTimestamp(message.createdAt)}
        </Text>
        {getSourceIcon(message.source) && (
          <Box
            as={getSourceIcon(message.source)!}
            aria-label={message.source}
            color={
              message.source === 'ai-note'
                ? 'var(--brand-base-color)'
                : 'fg.subtle'
            }
            fontSize="xs"
            display="inline-flex"
          />
        )}
      </HStack>
      <Text fontSize="xs" color="fg.muted" lineHeight="short">
        {preview}
      </Text>
    </Box>
  );
}
